import React from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import cn from 'classnames';
import localization from '../../shared/strings';
import { InputRange } from '../../UIComponents';

import normalizeFormatMimeType from '../../helpers/normalizeFormatMimeType';
import SelectOptions from './SelectOptions';
import {
  options,
  qualityConfig,
  pixelsConfig,
  defaultPresetConfig,
} from './config';
import handleSuggestImprovement from '../toolbars/handlers/handleSuggestImprovement';
import Tooltip from '../Tooltip';

function LockedSection({ locked, tooltip, children }) {
  if (!locked || !tooltip) {
    return children;
  }
  return (
    <Tooltip content={tooltip} placement="top">
      <span className={cn('assetsCopyDialog__lockedSection')}>
        {children}
      </span>
    </Tooltip>
  );
}

LockedSection.propTypes = {
  locked: PropTypes.bool,
  tooltip: PropTypes.node,
  children: PropTypes.node.isRequired,
};

LockedSection.defaultProps = {
  locked: false,
  tooltip: null,
};

const PresetsSettings = (props) => {
  const {
    selectedSettings,
    onChange,
    isDisabledFormat,
    disablePdfFormat,
    onBlur,
    sourceMimeType,
    omitResizeRow,
    lockedTooltip,
    settingsLocked,
    suggestImprovementLocation,
  } = props;
  const {
    quality = defaultPresetConfig.quality,
    resizing = defaultPresetConfig.resizing,
    width = defaultPresetConfig.width,
  } = selectedSettings;
  const rawMime = selectedSettings.mimeType;
  const mimeType = normalizeFormatMimeType(rawMime ?? defaultPresetConfig.mimeType);
  const isDefaultFormat = mimeType === defaultPresetConfig.mimeType;
  const isDefaultResizing = resizing === defaultPresetConfig.resizing;
  const isDisabledField = isDisabledFormat || isDefaultFormat;
  const isPngOutput = mimeType === 'image/png';
  const showLockedTooltip = settingsLocked && !isDisabledFormat;

  const formatButtons = [
    { label: 'jpg', value: 'image/jpeg' },
    { label: 'png', value: 'image/png' },
    { label: 'webp', value: 'image/webp' },
    { label: 'pdf', value: 'application/pdf', hidden: disablePdfFormat },
    { label: 'more', value: 'more' },
  ].filter((item) => !item.hidden);

  const handleFormatClick = (value) => {
    if (value === 'more') {
      const suggest = suggestImprovementLocation === localization.DOWNLOADDIALOG.suggestImprovementLocation
        ? localization.DOWNLOADDIALOG.suggestImprovement
        : localization.COPY_ASSETS_DIALOG.suggestImprovement;
      const initiationLocation = suggestImprovementLocation === localization.DOWNLOADDIALOG.suggestImprovementLocation
        ? 'Download Options Modal'
        : 'Copy Options Modal';
      handleSuggestImprovement(
        suggest.title,
        suggest.text,
        suggest.placeholder,
        suggestImprovementLocation ? { Location: suggestImprovementLocation } : {},
        initiationLocation,
      );
      return;
    }
    onChange({
      target: {
        name: 'mimeType',
        value,
      },
    });
  };

  const isFormatButtonActive = (button) => {
    const normalizedButton = normalizeFormatMimeType(button.value);
    const normalizedOutput = normalizeFormatMimeType(mimeType);

    if (normalizedOutput === normalizedButton) {
      return true;
    }

    if (mimeType === 'original' && sourceMimeType != null) {
      return normalizeFormatMimeType(sourceMimeType) === normalizedButton;
    }

    return false;
  };

  const handlePixelsKeyDown = (e) => {
    e.stopPropagation();
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const parsed = parseInt(String(width), 10);
    const base = Number.isFinite(parsed) ? parsed : pixelsConfig.min;
    const delta = e.key === 'ArrowUp' ? 1 : -1;
    const next = Math.min(
      pixelsConfig.max,
      Math.max(pixelsConfig.min, base + delta),
    );
    onChange({
      target: {
        type: 'number',
        name: 'width',
        value: next,
      },
    });
  };

  const resizeRow = (
    <div className="downloadDialog__settings__row">
      <SelectOptions
        label={localization.DOWNLOADDIALOG.labelSize}
        id="size"
        name="resizing"
        value={resizing}
        onChange={onChange}
        options={options.resizing}
        disabled={isDisabledField}
      />
      <TextField
        label={localization.DOWNLOADDIALOG.labelPixels}
        type="number"
        id="pixels"
        name="width"
        value={width}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={handlePixelsKeyDown}
        min={pixelsConfig.min}
        max={pixelsConfig.max}
        disabled={isDisabledField || isDefaultResizing}
        InputProps={{
          onBlur,
        }}
      />
    </div>
  );

  return (
    <div className="downloadDialog__settings">
      <If condition={!omitResizeRow}>
        <LockedSection locked={showLockedTooltip} tooltip={lockedTooltip}>
          {resizeRow}
        </LockedSection>
      </If>
      <LockedSection locked={showLockedTooltip} tooltip={lockedTooltip}>
        <Tooltip
          content={isPngOutput ? localization.DOWNLOADDIALOG.qualityHiddenPngNote : null}
          placement="top"
        >
          <InputRange
            label={localization.DOWNLOADDIALOG.labelQuality}
            value={quality}
            onChange={(value) => onChange({ target: { name: 'quality', value } })}
            min={qualityConfig.min}
            max={qualityConfig.max}
            disabled={isDisabledField || isPngOutput}
          />
        </Tooltip>
      </LockedSection>
      <LockedSection locked={showLockedTooltip} tooltip={lockedTooltip}>
        <div className={cn('assetsCopyDialog__format', isDisabledField && 'assetsCopyDialog__format--disabled')}>
          <div className="assetsCopyDialog__formatLabel">Format</div>
          <div className="assetsCopyDialog__formatButtons">
            {formatButtons.map((item) => {
              const isActive = isFormatButtonActive(item);

              return (
                <button
                  key={item.label}
                  className={`assetsCopyDialog__formatButton ${isActive ? 'is-active' : ''}`}
                  type="button"
                  onClick={() => handleFormatClick(item.value)}
                  disabled={isDisabledField}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </LockedSection>
    </div>
  );
};

PresetsSettings.propTypes = {
  selectedSettings: PropTypes.shape({
    mimeType: PropTypes.string,
    quality: PropTypes.number,
    resizing: PropTypes.string,
    width: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    density: PropTypes.string,
    units: PropTypes.string,
    isArchive: PropTypes.bool,
    organizeByCollections: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  isDisabledFormat: PropTypes.bool.isRequired,
  disablePdfFormat: PropTypes.bool.isRequired,
  sourceMimeType: PropTypes.string,
  omitResizeRow: PropTypes.bool,
  lockedTooltip: PropTypes.node,
  settingsLocked: PropTypes.bool,
  suggestImprovementLocation: PropTypes.string,
};

PresetsSettings.defaultProps = {
  sourceMimeType: undefined,
  omitResizeRow: false,
  lockedTooltip: null,
  settingsLocked: false,
  suggestImprovementLocation: null,
};

export default PresetsSettings;
